import { useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import { RefreshCw, Activity, Gauge, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

// Umbrales oficiales Google para Core Web Vitals
const CWV_THRESHOLDS = {
  lcp: { good: 2500, poor: 4000, label: 'LCP', desc: 'Largest Contentful Paint', unit: 'ms', fmt: (v) => `${(v / 1000).toFixed(2)}s` },
  inp: { good: 200, poor: 500, label: 'INP', desc: 'Interaction to Next Paint', unit: 'ms', fmt: (v) => `${Math.round(v)}ms` },
  cls: { good: 0.1, poor: 0.25, label: 'CLS', desc: 'Cumulative Layout Shift', unit: '', fmt: (v) => v.toFixed(2) },
  fcp: { good: 1800, poor: 3000, label: 'FCP', desc: 'First Contentful Paint', unit: 'ms', fmt: (v) => `${(v / 1000).toFixed(2)}s` },
  ttfb: { good: 800, poor: 1800, label: 'TTFB', desc: 'Time to First Byte', unit: 'ms', fmt: (v) => `${Math.round(v)}ms` },
};

function categorizar(metricKey, p75) {
  const t = CWV_THRESHOLDS[metricKey];
  if (!t || p75 == null) return 'unknown';
  if (p75 <= t.good) return 'good';
  if (p75 <= t.poor) return 'needs-improvement';
  return 'poor';
}

const COLORS = {
  good: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
  'needs-improvement': { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  poor: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
  unknown: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-500', dot: 'bg-gray-400' },
};

function CwvBadge({ metricKey, metric }) {
  const t = CWV_THRESHOLDS[metricKey];
  if (!t) return null;
  const cat = metric ? categorizar(metricKey, metric.p75) : 'unknown';
  const col = COLORS[cat];
  return (
    <div className={`rounded-lg border p-4 ${col.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-wide font-semibold text-gray-700">{t.label}</div>
          <div className="text-xs text-gray-500">{t.desc}</div>
        </div>
        <span className={`w-3 h-3 rounded-full ${col.dot}`} />
      </div>
      <div className={`text-2xl font-bold ${col.text}`}>
        {metric && metric.p75 ? t.fmt(metric.p75) : '—'}
      </div>
      {metric && metric.good > 0 && (
        <div className="mt-2 flex h-1.5 rounded overflow-hidden">
          <div style={{ width: `${metric.good * 100}%` }} className="bg-green-500" title={`Good ${(metric.good * 100).toFixed(0)}%`} />
          <div style={{ width: `${metric.needs_improvement * 100}%` }} className="bg-yellow-500" title={`Needs improvement ${(metric.needs_improvement * 100).toFixed(0)}%`} />
          <div style={{ width: `${metric.poor * 100}%` }} className="bg-red-500" title={`Poor ${(metric.poor * 100).toFixed(0)}%`} />
        </div>
      )}
    </div>
  );
}

export default function MarketingWebEstado() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUrl, setSelectedUrl] = useState(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingWebAPI.estado(30);
      setData(res.data);
      if (res.data?.crux?.length && !selectedUrl) setSelectedUrl(res.data.crux[0].url);
    } catch (e) {
      console.error('Error estado:', e);
      setError(e?.response?.data?.detail || 'Error cargando estado');
    } finally {
      setLoading(false);
    }
  };

  const ping = async () => {
    setSyncing(true);
    try {
      const res = await marketingWebAPI.estadoPing();
      alert(`Ping OK · HTTP ${res.data.status} · ${res.data.response_ms}ms`);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error en ping');
    } finally {
      setSyncing(false);
    }
  };

  const cruxSync = async () => {
    setSyncing(true);
    try {
      await marketingWebAPI.estadoCruxSync();
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error sync CrUX');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { cargar(); }, []);  // eslint-disable-line

  const uptime = data?.uptime;
  const cruxList = data?.crux || [];
  const selectedSnap = useMemo(() => cruxList.find((c) => c.url === selectedUrl) || cruxList[0], [cruxList, selectedUrl]);

  const responseTimeChart = useMemo(() => {
    if (!uptime?.daily_series?.length) return null;
    return {
      labels: uptime.daily_series.map((d) => d.date),
      datasets: [{
        label: 'Tiempo respuesta medio (ms)',
        data: uptime.daily_series.map((d) => d.avg_response_ms),
        borderColor: '#1A9169',
        backgroundColor: 'rgba(26, 145, 105, 0.12)',
        fill: true,
        tension: 0.3,
      }],
    };
  }, [uptime]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">Estado de la web</h1>
          <p className="text-sm text-gray-500 mt-1">
            Uptime + Core Web Vitals + estado de despliegue de <span className="font-mono">duettocatering.com</span>
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={cargar} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </button>
          <button onClick={ping} disabled={syncing} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50">
            Ping ahora
          </button>
          <button onClick={cruxSync} disabled={syncing} className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50">
            Sincronizar CrUX
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-yellow-800">
          {error}
        </div>
      )}

      {/* ---- UPTIME ---- */}
      <div className="bg-white border rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-duetto-primary" />
            <h2 className="text-base font-semibold text-gray-700">Uptime últimos 30 días</h2>
          </div>
          {uptime?.uptime_pct != null && (
            <div className="flex items-center gap-2">
              {uptime.uptime_pct >= 99.9 ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              )}
              <span className={`text-3xl font-bold ${uptime.uptime_pct >= 99.9 ? 'text-green-600' : 'text-yellow-600'}`}>
                {uptime.uptime_pct}%
              </span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500">Checks totales</div>
            <div className="text-xl font-bold">{uptime?.total_checks ?? '—'}</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500">Tiempo respuesta medio</div>
            <div className="text-xl font-bold">{uptime?.avg_response_ms != null ? `${uptime.avg_response_ms}ms` : '—'}</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500">Incidencias detectadas</div>
            <div className="text-xl font-bold">{uptime?.incidencias?.length ?? 0}</div>
          </div>
        </div>

        {responseTimeChart && (
          <div className="h-56 mb-4">
            <Line data={responseTimeChart} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, title: { display: true, text: 'ms' } } },
            }} />
          </div>
        )}

        {(uptime?.incidencias?.length ?? 0) > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Últimas incidencias</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Inicio</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Checks fallidos</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uptime.incidencias.map((inc, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{new Date(inc.inicio).toLocaleString('es-ES')}</TableCell>
                    <TableCell className="text-sm">{inc.duracion_min}min</TableCell>
                    <TableCell className="text-sm">{inc.checks_fallidos}</TableCell>
                    <TableCell className="text-xs font-mono text-red-600">{inc.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!loading && uptime?.total_checks === 0 && (
          <div className="text-sm text-gray-500 text-center py-4">
            Aún no hay checks. El ping automático arranca al deploy del ERP (cada 5 min).
            Pulsa <strong>Ping ahora</strong> para hacer uno manual.
          </div>
        )}
      </div>

      {/* ---- CORE WEB VITALS (CrUX) ---- */}
      <div className="bg-white border rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-duetto-primary" />
            <h2 className="text-base font-semibold text-gray-700">
              Core Web Vitals (Chrome reales · últimos 28 días)
            </h2>
          </div>
          {cruxList.length > 0 && (
            <select
              value={selectedUrl || ''}
              onChange={(e) => setSelectedUrl(e.target.value)}
              className="text-xs border rounded px-2 py-1 max-w-md font-mono"
            >
              {cruxList.map((c) => (
                <option key={c.url} value={c.url}>{c.url}</option>
              ))}
            </select>
          )}
        </div>

        {!loading && cruxList.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-6">
            Aún no hay datos CrUX sincronizados. Pulsa <strong>Sincronizar CrUX</strong>.
            <br />
            <span className="text-xs text-gray-400">
              Si después de sincronizar no aparecen datos, es porque la web aún tiene poco tráfico Chrome.
              CrUX necesita ~100 visitas únicas en 28 días para devolver datos. Suele tardar 2-4 semanas tras un dominio nuevo.
            </span>
          </div>
        )}

        {selectedSnap && (
          <>
            {!selectedSnap.has_data && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4 text-sm text-yellow-800">
                CrUX no tiene datos suficientes para esta URL (tráfico Chrome insuficiente en los últimos 28 días).
                Mira en Vercel Speed Insights para datos de tus visitantes mientras tanto.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <CwvBadge metricKey="lcp" metric={selectedSnap.lcp} />
              <CwvBadge metricKey="inp" metric={selectedSnap.inp} />
              <CwvBadge metricKey="cls" metric={selectedSnap.cls} />
              <CwvBadge metricKey="fcp" metric={selectedSnap.fcp} />
              <CwvBadge metricKey="ttfb" metric={selectedSnap.ttfb} />
            </div>
            <p className="text-xs text-gray-400 mt-3 text-right">
              Snapshot: {selectedSnap.fecha} · sincroniza automáticamente cada lunes a las 05:00
            </p>
          </>
        )}
      </div>

      {/* ---- INFO DEPLOY ---- */}
      <div className="bg-white border rounded-lg p-5">
        <h2 className="text-base font-semibold text-gray-700 mb-3">Despliegue + complementos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Vercel Speed Insights:</strong>
            <p className="text-gray-600 text-xs mt-1">
              Para métricas en vivo del 100% de tus visitantes (no solo Chrome), revisa el dashboard de Vercel → tu proyecto → pestaña "Speed Insights".
            </p>
          </div>
          <div>
            <strong>CrUX vs Speed Insights:</strong>
            <p className="text-gray-600 text-xs mt-1">
              CrUX (mostrado arriba) usa datos agregados de Chrome y es lo que Google usa para rankearte. Speed Insights mide a TODOS tus visitantes en tiempo real.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
